import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of, ReplaySubject } from 'rxjs';
import { tap, map, switchMap, take } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { Category, SpaceCategory, ApiListResponse, MastersResponse, SearchWallpaperResponse } from '../models/wallpaper.model';

const CACHE_TTL = 60 * 60 * 1000; // 1 hour in ms

/**
 * Bump this whenever a cached payload's SOURCE or SHAPE changes.
 *
 * Every cached entry lives in localStorage for an hour. Without a version in the
 * key, repointing an endpoint means every visitor who already has an entry keeps
 * being served the OLD backend's answer for up to an hour after the switch — on
 * the home page that is last week's prices, shown to a customer. Bumping the
 * version makes the existing entry unreachable, so the next load goes to the
 * network; the orphans fall out on their own TTL.
 *
 * v2 — wallpaper lists moved to the new backend (/api/site/wallpapers), and the
 *      per-sq-ft rate now resolves live instead of coming from a stored snapshot.
 * v3 — the PDP moved too. `ma_wp_{slug}` now holds a flat payload with a live
 *      "from" rate and a `surfaces` list, so an entry cached under v2 has both the
 *      wrong shape and a stale price.
 */
const CACHE_VERSION = 3;

/** Namespaced so a version bump rotates every key at once. */
const cacheKey = (key: string): string => `${key}.v${CACHE_VERSION}`;

function getCache(key: string): any {
  try {
    const raw = localStorage.getItem(cacheKey(key));
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw);
    if (Date.now() - ts > CACHE_TTL) { localStorage.removeItem(cacheKey(key)); return null; }
    return data;
  } catch { return null; }
}

function setCache(key: string, data: any): void {
  try { localStorage.setItem(cacheKey(key), JSON.stringify({ data, ts: Date.now() })); } catch { }
}

const MASTERS_MODELS = 'space, wallpaper-collection, wallpaper-theme, color ,category,sub-category';
const MASTERS_FILTERS = JSON.stringify({
  'space': [['wallpaper_status', '=', '1'], ['wallpaper', '=', '1']],
  'wallpaper-collection': [['status', '=', '1']],
  'wallpaper-theme': [['status', '=', '1']],
  'color': [['wallpaper_status', '=', '1']],
  'category': [['status', '=', '1']],
  'sub-category': [['status', '=', '1']],
});

@Injectable({ providedIn: 'root' })
export class WallpaperService {
  private base = environment.apiUrl;
  private apiUrlUnauth = environment.apiUrlUnauth;
  /**
   * Root of the NEW backend, which serves the panels and — as this module
   * migrates — the website too. Methods move across one at a time so anything not
   * yet moved keeps working off `base`, and reverting one is a single line.
   */
  private newBase = environment.newApiUrl;

  
  // Base URL — specific endpoints appended per method
  private searchBase = environment.searchApiBase;

  // Papers loaded once at startup, shared across all PDP pages
  private papers$ = new ReplaySubject<any>(1);
  private papersLoaded = false;

  constructor(private http: HttpClient) {
    this.preloadPapers();
  }

  private preloadPapers() {
    // MIGRATED. Was: GET {base}/api/unauth/paper?filter=[status,product_type_ids]
    //
    // That returned raw inventory rows, and the only price-like fields on them were
    // CSV strings packed by quality — `wallpaper_mrp_price: "0.56,3.36,5.05"`,
    // `wallpaper_rate: "0,499,799"`. Nothing on the row was a sellable ₹/sq ft, so
    // the PDP fell back to `print_cost × 144` — the INK AND MATERIAL COST, ₹12.64
    // where the real rate is ₹73 — and displayed that as the price.
    //
    // The new endpoint returns one resolved rate per surface off the paper margin
    // chain: `rate` (incl. GST), `rate_ex_tax` and the surface's own `gst_per`. No
    // CSV to parse, no cost mistaken for a price, and nothing for the client to
    // compute.
    //
    // Fetched once per page load into `papers$` and deliberately NOT written to
    // localStorage: a rate edited in Paper Pricing must show on the next load
    // rather than after an hour.
    this.http.get<any>(`${this.newBase}/api/site/wallpapers/surfaces`).subscribe({
      next: (res) => { this.papers$.next(res); this.papersLoaded = true; },
      error: () => { this.papers$.next({ data: [] }); this.papersLoaded = true; }
    });
  }

  getMasters(): Observable<MastersResponse> {
    const cached = getCache('ma_masters');
    if (cached) return of(cached);
    const params = new HttpParams()
      .set('models', MASTERS_MODELS)
      .set('filters', MASTERS_FILTERS);
    return this.http.get<MastersResponse>(`${this.apiUrlUnauth}/masters`, { params })
      .pipe(tap(res => setCache('ma_masters', res)));
  }

  getCategories(): Observable<ApiListResponse<Category>> {
    const cached = getCache('ma_categories');
    if (cached) return of(cached as ApiListResponse<Category>);
    const params = new HttpParams().set('pageSize', '200');
    return this.http.get<ApiListResponse<Category>>(`${this.base}/api/unauth/category`, { params })
      .pipe(tap(res => setCache('ma_categories', res)));
  }

  getSpaceCategories(): Observable<ApiListResponse<SpaceCategory>> {
    const cached = getCache('ma_spaces');
    if (cached) return of(cached as ApiListResponse<SpaceCategory>);
    const params = new HttpParams().set('pageSize', '200');
    return this.http.get<ApiListResponse<SpaceCategory>>(`${this.base}/api/unauth/space`, { params })
      .pipe(tap(res => setCache('ma_spaces', res)));
  }

  getPopularWallpapers(): Observable<ApiListResponse<any>> {
    // NOT cached in the browser, on purpose. This payload carries a live
    // per-sq-ft rate resolved off the paper margin chain, and that rate is edited
    // in the Paper Pricing panel. A localStorage copy kept showing the old price
    // for up to an hour after an edit — the exact staleness the server side was
    // built to avoid ("a margin edit reaches the grid immediately instead of
    // waiting out the TTL").
    //
    // MIGRATED to the new backend. Was:
    //   GET {base}/api/unauth/getAllSortedWallpapers?pageSize=200
    // Same designs in the same order — verified id-for-id against the old
    // endpoint. The per-sq-ft rate now resolves live off the paper margin chain
    // instead of the stored `wallpaper.price` snapshot, which the two systems had
    // been writing with different meanings.
    const params = new HttpParams().set('per_page', '200');
    return this.http.get<ApiListResponse<any>>(`${this.newBase}/api/site/wallpapers`, { params });
  }

  // getPopularWallpapers(): Observable<ApiListResponse<any>> {

  //   return this.http.get<ApiListResponse<any>>(
  //     `${this.base}/api/unauth/getAllSortedWallpapers`,
  //     {  }
  //   );
  // }


  // ── Wallpaper listing / search ────────────────────────────────────────────
  // GET {searchBase}/search_wallpaper.php
  // Param names match the actual Solr search API used by the original app
  searchWallpapers(filters?: {
    category?: string;     // comma-separated category IDs  e.g. "3" or "3,7"
    space?: string;        // comma-separated space IDs     e.g. "8"
    theme?: string;        // comma-separated theme IDs     e.g. "20"
    color?: string;        // comma-separated color names   e.g. "Red,Blue"
    type?: string;         // comma-separated types         e.g. "concept,pattern"
    quality?: string;      // comma-separated qualities     e.g. "standard,premium"
    searchtext?: string;   // keyword search
    sortby?: string;       // field to sort by: "id", "price", "updated_at", "recommended"
    sortdir?: string;      // "asc" | "desc"
    page?: number;
    images?: number;       // results per page (API uses "images" not "limit")
    pricelt?: number;      // min price
    pricegt?: number;      // max price
    vastu?: string;        // "yes" if vastu-friendly only
  }): Observable<SearchWallpaperResponse> {
    let params = new HttpParams();
    if (filters?.category) params = params.set('category', filters.category);
    if (filters?.space) params = params.set('space', filters.space);
    if (filters?.theme) params = params.set('theme', filters.theme);
    if (filters?.color) params = params.set('color', filters.color);
    if (filters?.type) params = params.set('type', filters.type);
    if (filters?.quality) params = params.set('quality', filters.quality);
    if (filters?.searchtext) params = params.set('searchtext', filters.searchtext);
    if (filters?.sortby) params = params.set('sortby', filters.sortby);
    if (filters?.sortdir) params = params.set('sortdir', filters.sortdir);
    if (filters?.page !== undefined) params = params.set('page', filters.page.toString());
    if (filters?.images) params = params.set('images', filters.images.toString());
    if (filters?.pricelt) params = params.set('pricelt', filters.pricelt.toString());
    if (filters?.pricegt) params = params.set('pricegt', filters.pricegt.toString());
    if (filters?.vastu) params = params.set('vastu', filters.vastu);

    // Solr stays the SEARCH engine — it is the only thing that can rank 725
    // designs by keyword, theme, space and colour — but its `price` is dropped on
    // the way out, and that is not a tidy-up. The index carries the
    // `wallpaper.price` snapshot, a per-DESIGN number from the old model: ₹99 for
    // one design, ₹149 for another, where the live rate is ₹73 for every one of
    // them. We charge for the PAPER, not the design, so a per-design price is not
    // stale so much as no longer a thing that exists.
    //
    // Overridden here rather than in each caller because there are seven — the
    // listing, category and space pages, the similar dialog, the PDP rail, the
    // header and NUR — and one of them forgetting is a wrong price on screen.
    return this.http.get<SearchWallpaperResponse>(`${this.searchBase}/search_wallpaper.php`, { params })
      .pipe(switchMap(res => this.liveFromRate().pipe(
        map(rate => ({
          ...res,
          results: (res?.results ?? []).map(w => ({ ...w, price: rate })),
        }))
      )));
  }

  /**
   * The live "from" ₹/sq ft, tax-inclusive: the cheapest surface that can be
   * priced.
   *
   * Reads the surfaces `preloadPapers()` already fetched, so this adds no request
   * and cannot disagree with the rate the PDP quotes — both resolve off the same
   * paper margin chain. Null when nothing can be priced, which callers must render
   * as "no price" rather than zero.
   */
  private liveFromRate(): Observable<number | null> {
    return this.papers$.pipe(
      take(1),
      map((res: any) => {
        // `rate` is null on a surface whose margin chain is incomplete — those
        // cannot be quoted at all, so they must not set the "from" figure.
        const rates = (res?.data ?? [])
          .map((p: any) => Number(p?.rate))
          .filter((r: number) => Number.isFinite(r) && r > 0);
        // The endpoint sorts ascending, but min() means a change there cannot
        // quietly turn the "from" price into the most expensive surface.
        return rates.length ? Math.min(...rates) : null;
      })
    );
  }

  // ── Art Ready listing ─────────────────────────────────────────────────────
  // GET {searchBase}/search_on_sale.php
  searchArtReadyItems(filters?: {
    search?: string;
    categoryId?: number;
    page?: number;
    limit?: number;
    sort?: string;
  }): Observable<any> {
    let params = new HttpParams();
    if (filters?.search) params = params.set('search', filters.search);
    if (filters?.categoryId) params = params.set('category_id', filters.categoryId.toString());
    if (filters?.sort) params = params.set('sort', filters.sort);
    if (filters?.page !== undefined) params = params.set('page', filters.page.toString());
    if (filters?.limit) params = params.set('limit', filters.limit.toString());
    return this.http.get<any>(`${this.searchBase}/search_on_sale.php`, { params });
  }

  getOnSaleSolr(query: string): Observable<any> {
    return this.http.get<any>(`${this.searchBase}/search_on_sale.php?${query}`);
  }

  // ── PDP endpoints (main backend) ─────────────────────────────────────────
  getWallpaperBySlug(slug: string): Observable<any> {
    // MIGRATED. Was: GET {base}/api/unauth/wallpaper/${slug}
    //
    // That route did `Wallpaper::find($id)` — an ID lookup — so passing a slug to
    // it returned a 500 on every design. The new endpoint takes the slug the URL
    // actually carries (and still accepts an id). Response stays FLAT with
    // `colors_arr`, so nothing downstream changes.
    return this.http.get<any>(`${this.newBase}/api/site/wallpapers/${slug}`);
  }

  getWallpaperById(id: string | number): Observable<any> {
    // MIGRATED. Was: GET {base}/api/unauth/wallpaperId/${id}
    //
    // That route 500'd on any design with `parent_wallpaper = 0` — 1554 of 1873
    // rows — because it dereferenced the parent unconditionally. The new detail
    // endpoint takes a slug OR an id, so both PDP entry points share one route.
    return this.http.get<any>(`${this.newBase}/api/site/wallpapers/${id}`);
  }

  getSimilarWallpapers(params: { slug?: string; wallpaper_id?: string; id?: number }, limit = 12): Observable<any> {
    // MIGRATED. Was: GET {base}/api/unauth/similar-wallpapers?slug|wallpaper_id|id
    //
    // The design is now part of the path, so one identifier instead of three
    // optional ones. Slug is preferred because the PDP already has it; the id is
    // the fallback for a caller that only knows that.
    const key = params.slug ?? (params.id != null ? String(params.id) : params.wallpaper_id ?? '');
    const p = new HttpParams().set('limit', String(limit));
    return this.http.get<any>(`${this.newBase}/api/site/wallpapers/${key}/similar`, { params: p });
  }

   initWallpaperCustomization(slug: string): Observable<any> {
    // MIGRATED. Was: GET {base}/api/unauth/init-wallpaper-customization?slug=
    //
    // Despite the name this is the PDP's PRIMARY loader — it is what sets
    // `this.wallpaper`. It now reads the new detail endpoint, which answers FLAT
    // (so the component's `data?.id ? data : null` branch takes it unchanged) and
    // carries `colors_arr`, a `surfaces` list with live rates, and a "from" price.
    //
    // NOT cached in the browser: the payload holds a rate that Paper Pricing can
    // change, and a stored copy would hide that change for an hour.
    //
    // `perSqFtInstallation` is intentionally absent. The component only applies it
    // behind an `if`, and an installation rate is per CITY — it belongs to the
    // pincode check (`rate_per_sqft`), not to a design.
    return this.http.get<any>(`${this.newBase}/api/site/wallpapers/${slug}`);
  }

  getPapers(paperIds: number[]): Observable<any> {
    const filter = JSON.stringify([
      ['status', '=', 1],
      ['product_type_ids', 9],
      ['ids', paperIds],
    ]);
    const params = new HttpParams()
      .set('filter', filter)
      .set('orderBy', '[]')
      .set('pageNumber', '0')
      .set('pageSize', '-1');
    return this.http.get<any>(`${this.base}/api/unauth/paper`, { params });
  }

  // Fetch all active papers (no ID filter — for custom upload)
  getAllPapers(): Observable<any> {
    // Returns instantly if already loaded (from preload)
    return this.papers$.asObservable();
  }

  // ── Installation pincode check ──────────────────────────────────────────
  checkInstallationPincode(pincode: string): Observable<any> {
    // MIGRATED. Was: GET {base}/api/unauth/checkInstallationPincode?pincode=
    //
    // The new endpoint is the SAME one the React panel calls — not a copy — so
    // "serviceable" can only ever mean one thing. It answers with the city, the
    // ₹/sq ft, the minimum charge and the expert-visit fee from Production's own
    // rates, where the old one answered `{ exist: bool }` and hid the rate until
    // an order was priced.
    //
    // The old `exist` flag is gone; callers read `serviceable` instead.
    const params = new HttpParams().set('pincode', pincode);
    return this.http.get<any>(`${this.newBase}/api/site/service/check-pincode`, { params });
  }

  /**
   * The expert-visit policy: the "from" fee, the minimum wallpaper order the fee
   * is adjusted against, and how long the visit stays valid.
   *
   * NEW — there was no endpoint for this because the numbers were literals in the
   * PDP template and in the booking payload's caption. Not cached: this is a
   * business rule Production can change, and a stale terms line is a stale
   * promise to the customer.
   */
  getExpertVisitTerms(): Observable<any> {
    return this.http.get<any>(`${this.newBase}/api/site/service/visit-terms`);
  }

  // ── Price calculation ───────────────────────────────────────────────────
  calculateCustomWallpaperPrice(params: Record<string, any>): Observable<any> {
    // MIGRATED. Was: POST {base}/api/unauth/calculateCustomWallpaperPrice
    //
    // This is the important one. The new endpoint runs the SAME quote the cart
    // line is written from, so the page cannot show a price the cart then
    // disagrees with — which is exactly what used to happen (₹13,275 quoted
    // against a ₹15,923 base stored).
    //
    // It answers in the old key names (printRate, amount_payable, discount_rate,
    // installation_amount …) so nothing downstream needs renaming. Real
    // changes: `installation_tax` is installation's 18% GST on top of its charge,
    // the rate comes from the paper margin chain rather than a hardcoded matrix,
    // and `breakdown` (per wall) / `summary` (cart) carry the forward ex-GST
    // figures — base, discount, taxable, CGST/SGST, line and grand totals.
    return this.http.post<any>(`${this.newBase}/api/site/wallpapers/quote`, params);
  }

  // ── Art Ready detail (main backend) ──────────────────────────────────────
  getArtReadyItemById(id: number | string): Observable<any> {
    return this.http.get<any>(`${this.base}/api/unauth/on_sale/${id}`);
  }

  getArtReadyItemByCaption(caption: string): Observable<any> {
    return this.http.get<any>(`${this.base}/api/unauth/on_sale_caption/${encodeURIComponent(caption)}`);
  }

  // ── Featured frame endpoints (main backend) ───────────────────────────────
  getFeaturedFrameDetails(id: number | string): Observable<any> {
    return this.http.get<any>(`${this.base}/api/unauth/get-featured-frame/${id}`);
  }

  getFeaturedFrameCustomization(): Observable<any> {
    return this.http.get<any>(`${this.base}/api/unauth/featured-frame-customization`);
  }

  getFeaturedFramePrice(params?: Record<string, any>): Observable<any> {
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach(k => {
        if (params[k] !== undefined && params[k] !== null) {
          httpParams = httpParams.set(k, params[k].toString());
        }
      });
    }
    return this.http.get<any>(`${this.base}/api/unauth/getFeaturedFramePrice`, { params: httpParams });
  }

  // ── Wall Art page endpoints ───────────────────────────────────────────────
  getWallArtMeta(): Observable<any> {
    return this.http.get<any>(`${this.base}/api/unauth/meta/_wall-art`);
  }

  getSetting(): Observable<any> {
    return this.http.get<any>(`${this.base}/api/unauth/setting`);
  }

  getRequirements(): Observable<any> {
    const params = new HttpParams()
      .set('filter', '[]')
      .set('orderBy', '[]')
      .set('pageNumber', '0')
      .set('pageSize', '-1');
    return this.http.get<any>(`${this.base}/api/unauth/requirement`, { params });
  }

  getWallArtFeaturedProducts(userId: number = 0): Observable<any> {
    const filter = JSON.stringify([
      ['featured', '=', 1],
      ['product', 'wall_art'],
      ['status', '=', 1],
      ['wishlist_item.user_id', userId],
    ]);
    const orderBy = JSON.stringify([
      ['on_sale.popular_art', 'desc'],
      ['on_sale.created_at', 'desc'],
    ]);
    const params = new HttpParams()
      .set('type', 'list')
      .set('filter', filter)
      .set('orderBy', orderBy)
      .set('pageNumber', '0')
      .set('pageSize', '50');
    return this.http.get<any>(`${this.base}/api/unauth/on_sale`, { params });
  }

  // ── Thumbnail loading — main backend (home page category/space cards) ─────
  getWallpapers(filters?: {
    categoryId?: number; spaceId?: number; search?: string;
    pageNumber?: number; pageSize?: number;
  }): Observable<ApiListResponse<any>> {
    let params = new HttpParams();
    if (filters?.categoryId) params = params.set('category_id', filters.categoryId.toString());
    if (filters?.spaceId) params = params.set('space_id', filters.spaceId.toString());
    if (filters?.search) params = params.set('search', filters.search);
    if (filters?.pageNumber !== undefined) params = params.set('pageNumber', filters.pageNumber.toString());
    if (filters?.pageSize) params = params.set('pageSize', filters.pageSize.toString());
    return this.http.get<ApiListResponse<any>>(`${this.base}/api/unauth/wallpaper`, { params });
  }

  detectFiltersAI(text: string) {

    return this.http.post<any>(
      this.apiUrlUnauth + '/ai-filters',
      { text: text }
    )

  }

  detectFiltersImage(imageData: string) {
    return this.http.post<any>(
      this.apiUrlUnauth + '/detectImageFilters',
      { image: imageData }
    );
  }

  getExclusiveWallpapers(limit: number = 20): Observable<any> {
    // NOT cached — see getPopularWallpapers(): the rate is live and panel-editable.
    //
    // MIGRATED to the new backend. Was:
    //   GET {apiUrlUnauth}/exclusive-wallpapers?limit=N
    // `quality` is an explicit filter now rather than a dedicated endpoint, so one
    // listing method serves both home rails.
    const params = new HttpParams()
      .set('quality', 'exclusive')
      .set('per_page', String(limit));
    return this.http.get<any>(`${this.newBase}/api/site/wallpapers`, { params });
  }

  parseSearch(query: string): Observable<{
    mode: 'text' | 'filters';
    searchtext: string;
    space_ids?: number[];
    theme_ids?: number[];
    category_ids?: number[];
    color_names?: string[];
    type?: 'concept' | 'pattern' | null;
    vastu?: boolean;
  }> {
    return this.http.post<any>(this.apiUrlUnauth + '/parse-search', { query });
  }

  getWallArtCategories(catSlug: string, type: string) {
    return this.http.get<any[]>(this.base + '/api/unauth/wallart-category-list?cat_slug=' + catSlug + '&type=' + type);
  }

  // ── Landing page endpoints ──────────────────────────────────────────────
  getHomeSettings(): Observable<any> {
    return this.http.get<any>(`${this.base}/api/unauth/home-setting`);
  }

  getTestimonials(): Observable<any> {
    const params = new HttpParams()
      .set('filter', JSON.stringify([['status', '=', 1]]))
      .set('orderBy', JSON.stringify([['id', 'desc']]))
      .set('pageNumber', '0')
      .set('pageSize', '10');
    return this.http.get<any>(`${this.base}/api/unauth/testimonial`, { params });
  }

  getFrames(filters?: any): Observable<any> {
    let params = new HttpParams()
      .set('filter', JSON.stringify(filters || [['status', '=', 1], ['best_seller', '=', 1]]))
      .set('orderBy', JSON.stringify([['id', 'desc']]))
      .set('pageNumber', '0')
      .set('pageSize', '10');
    return this.http.get<any>(`${this.base}/api/unauth/frame`, { params });
  }

  getMounts(): Observable<any> {
    const params = new HttpParams()
      .set('filter', JSON.stringify([['status', '=', 1], ['best_seller', '=', 1]]))
      .set('orderBy', JSON.stringify([['id', 'desc']]))
      .set('pageNumber', '0')
      .set('pageSize', '10');
    return this.http.get<any>(`${this.base}/api/unauth/mount`, { params });
  }

}
